import React, { useCallback } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues, FormikErrors } from "formik";
import { bindActionCreators, Dispatch } from "redux";

import { Form, PickerInput, DataColumnDef, ITextQuery, IDataColumnDefProps, ValidationMessage } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { releaseMessages } from "../../release.localization";
import { releaseDetailsManagerActions } from "../ReleaseDetails.actions";
import { IReleaseDetailsState, ITermData, ITerm } from "../ReleaseDetails.state";

import ReleaseTermsGrid from "./ReleaseTermsGrid";

export interface IReleaseTermsFormProps {
  termsPickerData: ITerm[];
  newStagedTerm: ITerm[];
  associatedTerms: ITermData[];
  status: string;
  loadPickerTerms: (criterion: IPickerSearchCriterion<ITerm>) => void;
  selectNewTerm: (terms: ITerm[]) => void;
}

const ReleaseTermsForm: React.FC<IReleaseTermsFormProps> = ({ termsPickerData, newStagedTerm, associatedTerms, status, loadPickerTerms, selectNewTerm }) => {
  const localizedValues = useLocalizedValues(() => ({
    pickerDialogTitle: releaseMessages.heading("releaseTerms"),
    termTitle: displayMessages.property("term"),
    descriptionTitle: displayMessages.property("description"),
    termsSectionTitle: displayMessages.heading("associatedTerms"),
    managedLinksLabel: releaseMessages.property("manageTerms"),
    messageAndTermAreRelatedError: validationMessages.error("termAndReleaseAlreadyRelated")
  }));

  const localizer = useLocalizer();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexReleaseManager || status !== releaseMessages.option("releaseStatusPlanning").value;

  const onTermChange = useCallback(
    async (rows: ITerm[], validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>, setFieldValidationValues: (values: FormikValues) => void) => {
      selectNewTerm(rows);
      setFieldValidationValues({
        newStagedTerm: rows
      });
      await validateForm();
    },
    [selectNewTerm]
  );

  const onValidate = useCallback(
    (values: FormikValues) => {
      const errors: any = {};
      if (values.newStagedTerm.length === 0) {
        errors.manageLinksPicker = localizer(validationMessages.error("propertyIsRequired"), {
          property: localizedValues.managedLinksLabel
        });
      }
      return errors;
    },
    [localizedValues.managedLinksLabel, localizer]
  );

  const renderPicker = (
    errors: FormikErrors<FormikValues>,
    handleBlur: (e: React.FocusEvent<any>) => void,
    validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>,
    setValues: (values: FormikValues) => void
  ) => {
    return (
      <PickerInput
        multiSelect={true}
        name="termsPicker"
        dialogTitle={localizedValues.pickerDialogTitle}
        keySelector={(row: ITerm) => row.termId}
        displaySelector={(row: ITerm) => row.term}
        selected={newStagedTerm}
        data={termsPickerData}
        onBlur={handleBlur}
        disabled={isReadOnlyMode}
        onSelectionChanged={rows => onTermChange(rows as ITerm[], validateForm, setValues)}
        onSearch={(query: ITextQuery, columns: Array<IDataColumnDefProps<ITerm>>) => loadPickerTerms({ query: query, searchedColumns: columns })}
      >
        <DataColumnDef<ITerm> key="1" id="term" searchable={true} sortable={true} title={localizedValues.termTitle} valueSelector={row => row.term}>
          {(row: ITerm) => row.term}
        </DataColumnDef>
        <DataColumnDef<ITerm> key="2" id="definition" searchable={true} sortable={true} title={localizedValues.descriptionTitle} valueSelector={row => row.definition}>
          {(row: ITerm) => row.definition}
        </DataColumnDef>
      </PickerInput>
    );
  };

  return (
    <Formik
      initialValues={{
        newStagedTerm: []
      }}
      validate={onValidate}
      onSubmit={(_: FormikValues, { setSubmitting }) => {
        setSubmitting(false);
      }}
    >
      {({ handleBlur, errors, setValues, validateForm }) => (
        <>
          <Form.Row id="terms-picker">
            <Form.FieldPair labelText={localizedValues.managedLinksLabel}>
              {renderPicker(errors, handleBlur, validateForm, setValues)}
              <ValidationMessage message={errors.manageLinksPicker ? (errors.manageLinksPicker as string) : ""} />
            </Form.FieldPair>
          </Form.Row>
          {associatedTerms.length > 0 && (
            <Form.Row>
              <ReleaseTermsGrid />
            </Form.Row>
          )}
        </>
      )}
    </Formik>
  );
};

export default connect(
  (state: { releaseDetails: IReleaseDetailsState }) => {
    return {
      newStagedTerm: state.releaseDetails.newStagedTerm,
      termsPickerData: state.releaseDetails.termsPickerData,
      notifications: state.releaseDetails.notifications,
      associatedTerms: state.releaseDetails.associatedTerms,
      status: state.releaseDetails.releaseStatus
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectNewTerm: releaseDetailsManagerActions.selectNewTerm,
        loadPickerTerms: releaseDetailsManagerActions.loadPickerTerms
      },
      dispatch
    )
)(ReleaseTermsForm);
